import type { Accessor } from "solid-js";
import { isBillingEnabled } from "@/domains/billing/utils/is-billing-enabled";
import {
  type AccessState,
  resolveAccessState,
} from "@/domains/company/anonymization/access-state";
import { useAnonymizationCapabilities } from "@/domains/company/anonymization/api/capabilities";
import {
  useAnonymousCvEnabled,
  useCandidateReportEnabled,
} from "@/domains/company/anonymization/feature-flags";

export type { AccessState };

/**
 * Whether the anonymous-CV action can be offered to this user, and how.
 *
 * Combines the three signals that decide it — the environment's kill switch,
 * whether billing exists here at all, and the plan entitlement — through
 * {@link resolveAccessState}, which owns the precedence and is unit-tested on
 * its own.
 */
export const useAnonymousCvAccess = (): Accessor<AccessState> => {
  const featureEnabled = useAnonymousCvEnabled();
  const capabilities = useAnonymizationCapabilities();

  return () =>
    resolveAccessState({
      featureEnabled: featureEnabled(),
      billingEnabled: isBillingEnabled(),
      entitled: capabilities.data?.anonymous_cv,
    });
};

/** Same, for the AI candidate report. */
export const useCandidateReportAccess = (): Accessor<AccessState> => {
  const featureEnabled = useCandidateReportEnabled();
  const capabilities = useAnonymizationCapabilities();

  return () =>
    resolveAccessState({
      featureEnabled: featureEnabled(),
      billingEnabled: isBillingEnabled(),
      entitled: capabilities.data?.candidate_report,
    });
};
